import { useState, useRef, useEffect, useCallback } from 'react';
import useDebounce from 'utils/useDebounce';

export default function useDebouncedInput(value, onChange, delay = 300) {
  const [localValue, setLocalValue] = useState(value);
  const valueRef = useRef(value);
  // Track the last value we sent to parent to distinguish our debounced changes
  // from external changes (e.g., reset button, URL navigation)
  const lastSentValueRef = useRef(value);

  // Wrap onChange to track what we send to parent
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const wrappedOnChange = useCallback((ev, rawValue) => {
    // Track the value we're sending to parent
    const sentValue = ev?.target ? ev.target.value : ev;
    lastSentValueRef.current = sentValue;
    onChangeRef.current(ev, rawValue);
  }, []);

  const debouncedChange = useDebounce(wrappedOnChange, delay);

  function onChangeHandler(ev, rawValue) {
    if (ev.persist) {
      ev.persist();
    }
    const v = ev?.target ? ev.target.value : ev;

    valueRef.current = v;
    setLocalValue(v);
    debouncedChange.cancel();
    debouncedChange(ev, rawValue);
  }

  useEffect(() => {
    // Only sync from parent if it's an external change.
    // If the parent value matches what we last sent, it's our own debounced change
    // coming back - don't sync as the local value may have moved ahead.
    if (valueRef.current !== value && lastSentValueRef.current !== value) {
      valueRef.current = value;
      lastSentValueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  return [localValue, onChangeHandler];
}
